import React, { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ChartErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Chart Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || <DefaultErrorFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

function DefaultErrorFallback({ error }: { error?: Error }) {
  return (
    <div className="chart-container" style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '450px', 
      backgroundColor: 'transparent',
      border: '1px dashed #ccc',
      borderRadius: '8px',
      padding: '20px',
      textAlign: 'center'
    }}>
      <div style={{ color: '#666', fontSize: '18px', marginBottom: '10px' }}>
        📊 Chart temporarily unavailable
      </div>
      <div style={{ color: '#999', fontSize: '14px' }}>
        Please try refreshing the page or check your inputs
      </div>
      {process.env.NODE_ENV === 'development' && error && (
        <details style={{ marginTop: '20px', fontSize: '12px', color: '#999' }}>
          <summary>Error details (dev mode)</summary>
          <pre style={{ textAlign: 'left', overflow: 'auto', maxWidth: '100%' }}>
            {error.message}
          </pre>
        </details>
      )}
    </div>
  );
}

export default function ChartErrorBoundary({ children, fallback }: Props) {
  return (
    <ChartErrorBoundaryClass fallback={fallback}>
      {children}
    </ChartErrorBoundaryClass>
  );
}